import { useContext, useState } from 'react';
import {
  Avatar,
  Box,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ProgressSpinner,
  Text,
  useResponsive,
} from '@bitrise/bitkit';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import { Account } from '@/models/Account';
import useSwitchAccount from '@/hooks/useSwitchAccount';
import { navigateToUrl } from '@/utils/navigation';
import WorkspaceList from './WorkspaceSelectorPopover';

type WorkspaceSelectorProps = {
  isOpen?: boolean;
  onOpen: () => void;
  onClose: () => void;
};
const WorkspaceSelector = ({ isOpen, onOpen, onClose }: WorkspaceSelectorProps): JSX.Element => {
  const { account } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;
  const [selectedAccount, setSelectedAccount] = useState<Account>(account);
  const [workspaceChangeInProgress, setWorkspaceChangeInProgress] = useState(false);
  const { mutate: switchAccount } = useSwitchAccount();
  const { isMobile } = useResponsive();

  const onWorkspaceSelect = (newSelectedWorkspace: Account) => {
    setWorkspaceChangeInProgress(true);
    switchAccount(
      { slug: newSelectedWorkspace.slug },
      {
        onSuccess: (response) => {
          setSelectedAccount(newSelectedWorkspace);
          navigateToUrl(response.headers.location);
        },
        onSettled: () => {
          onClose();
          setWorkspaceChangeInProgress(false);
        },
      },
    );
  };

  const WorkspaceSelectorTrigger = (
    <Box
      as="button"
      display="flex"
      alignContent="space-between"
      alignItems="center"
      cursor="pointer"
      marginLeft={[0, 16]}
      paddingY={[12, 0]}
      paddingX={[24, 0]}
      width={['100%', 'auto']}
    >
      {workspaceChangeInProgress && (
        <Box paddingX="8" marginRight="20">
          <ProgressSpinner textColor="white" />
        </Box>
      )}
      {!workspaceChangeInProgress && (
        <Avatar
          className="platform-shell-workspace"
          marginRight={[12, 0]}
          size="40"
          borderWidth={1}
          borderColor="neutral.90"
          name={selectedAccount.name}
          src={selectedAccount.avatarUrl}
          variant="workspace"
        />
      )}
      <Box position="absolute" left="77" textAlign="left" width={[188, 145]}>
        <Text fontWeight="bold" size="1" textTransform="uppercase" color="neutral.60">
          Workspace
        </Text>
        <Text
          size="3"
          fontWeight="normal"
          color={isMobile ? '' : 'neutral.80'}
          hasEllipsis
          title={selectedAccount.name}
        >
          {selectedAccount.name}
        </Text>
      </Box>
      <Icon
        position="absolute"
        right="16"
        name={isOpen ? 'ChevronUp' : 'ChevronDown'}
        textColor={['purple.10', 'neutral.100']}
      />
    </Box>
  );

  if (isMobile) {
    return (
      <Box width={[320, 'auto']}>
        <Box id="trigger" onClick={() => (isOpen ? onClose() : onOpen())} background={isOpen ? 'neutral.95' : ''}>
          {WorkspaceSelectorTrigger}
        </Box>
        {isOpen && (
          <Box
            id="content"
            background="neutral.100"
            position="absolute"
            left="0"
            top="100%"
            zIndex="2"
            width="100%"
            height={['calc(100vh - 70px)', '']}
          >
            <WorkspaceList onSelect={onWorkspaceSelect} />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Popover isLazy placement="bottom-start" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>{WorkspaceSelectorTrigger}</PopoverTrigger>
      {!workspaceChangeInProgress && (
        <PopoverContent width="360px" textColor="purple.10">
          <WorkspaceList onSelect={onWorkspaceSelect} />
        </PopoverContent>
      )}
    </Popover>
  );
};

export default WorkspaceSelector;
